import { useSearchParams } from 'react-router-dom'
import { useCallback, useEffect } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isFailed, isSuccess } from 'redux/toolkit/api'
import { exchangeCodeForTokens, resetTokenExchange, clearOAuthStateAction } from 'redux/features/bccAuth/bccAuthSlice'
import { getStoredOAuthState, getStoredCodeVerifier, parseState } from 'lib/oauth2/bccOAuth2Utils'
import uiPaths from 'config/uiPaths'
import logger from 'lib/logger'

const EMAIL_LOG_PATH = `/admin/${uiPaths.EMAIL_LOG.path}`

function getReturnPath(stateParam: string | null): string {
  if (!stateParam) {
    return EMAIL_LOG_PATH
  }
  const parsed = parseState(stateParam)
  if (parsed?.returnPath) {
    try {
      // new URL() normalizes ../, collapses //, resolves path traversal
      const url = new URL(parsed.returnPath, window.location.origin)

      // After normalization, check that:
      // 1. The origin hasn't changed (prevents absolute redirects to evil.com)
      // 2. The normalized pathname starts with /admin/
      if (url.origin === window.location.origin && url.pathname.startsWith('/admin/')) {
        return url.pathname + url.search + url.hash
      }
    } catch {
      // URL constructor threw — invalid URL, fall through to default
    }
  }
  return EMAIL_LOG_PATH
}

export const useBccOAuth2CallbackLogic = () => {
  const dispatch = useAppDispatch()
  const [params] = useSearchParams()
  const code = params.get('code')
  const stateParam = params.get('state')
  const error = params.get('error')
  const errorDescription = params.get('error_description')

  const { isTokenExchangeSuccess, isTokenExchangeFailed } = useAppSelector(_store => ({
    isTokenExchangeSuccess: isSuccess(_store.bccAuth.api.tokenExchangeStatus),
    isTokenExchangeFailed: isFailed(_store.bccAuth.api.tokenExchangeStatus)
  }))

  // Full page navigation is required because the callback lives in the
  // enduser app (/webui/redirect) while the email log is in the admin app
  // (/admin/email-log). React Router's navigate() cannot cross app boundaries.
  const redirectToReturnPath = useCallback(
    (errorParam?: string) => {
      const returnPath = getReturnPath(stateParam)
      if (errorParam) {
        const redirectUrl = new URL(returnPath, window.location.origin)
        redirectUrl.searchParams.set('bcc_auth_error', errorParam)
        window.location.href = `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`
      } else {
        window.location.href = returnPath
      }
    },
    [stateParam]
  )

  // Cleanup on unmount
  useEffect(
    () => () => {
      dispatch(resetTokenExchange())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // Handle initial callback
  useEffect(() => {
    // If there's an error from the auth server
    if (error) {
      logger.error(
        `[BCC OAuth2 Callback] Auth server returned error: ${error} - ${errorDescription || 'no description'}`
      )

      dispatch(clearOAuthStateAction())
      // Redirect with error code only (useBccAuthErrorNotifications maps codes)
      redirectToReturnPath(error)
      return
    }

    // If we have a code, validate state and exchange for token
    if (code && stateParam) {
      const storedState = getStoredOAuthState()

      // Validate state parameter (CSRF protection)
      if (!storedState || storedState !== stateParam) {
        logger.error(
          `[BCC OAuth2 Callback] State validation failed - possible CSRF attempt (hasStoredState: ${!!storedState}, statesMatch: ${
            storedState === stateParam
          })`
        )
        dispatch(clearOAuthStateAction())
        redirectToReturnPath('invalid_state')
        return
      }

      const codeVerifier = getStoredCodeVerifier()
      if (!codeVerifier) {
        logger.error('[BCC OAuth2 Callback] Missing code verifier - PKCE flow incomplete')
        dispatch(clearOAuthStateAction())
        redirectToReturnPath('missing_code_verifier')
        return
      }

      dispatch(exchangeCodeForTokens({ code, codeVerifier }))
      return
    }

    // If neither code nor error, something is wrong
    if (!code && !error) {
      logger.error('[BCC OAuth2 Callback] Missing code parameter in callback')
      redirectToReturnPath('missing_code')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle token exchange success
  useEffect(() => {
    if (isTokenExchangeSuccess) {
      redirectToReturnPath()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenExchangeSuccess])

  // Handle token exchange failure
  useEffect(() => {
    if (isTokenExchangeFailed) {
      redirectToReturnPath('token_exchange_failed')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenExchangeFailed])
}
